import { useState, useCallback } from "react";
import { ApiDataResponse, ApiResponse, Student } from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useStudentsContext } from "../../Contexts/StudentsContext";
import { useApiResponseHandler } from "../useApiResponseHandler";

// Type for student creation/update (without system fields)
type StudentFormData = Omit<
  Student,
  | "id"
  | "schoolId"
  | "createdAt"
  | "updatedAt"
  | "avatarUrl"
  | "publicId"
  | "guardians"
>;

export const useStudentService = () => {
  const { setStudents, setSelectedStudent } = useStudentsContext();
  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  const createStudent = useCallback(
    async (
      student: StudentFormData,
      onSuccess?: (student: Student) => void
    ): Promise<{ result: ApiDataResponse<Student> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        // Convert StudentFormData to Student for API call
        const studentForApi = student as unknown as Student;
        const result = await new ApiService<Student>(
          "/students",
          axiosClient
        ).create(studentForApi);
        if (result.succeeded) {
          setStudents((prev: Student[]) => [...prev, result.data]);
          setSelectedStudent(result.data);
          handleApiSuccess("Student created successfully");
          onSuccess?.(result.data);
          return { result };
        } else {
          handleApiError(result, "Failed to create student");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to create student");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to create student",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: {} as Student,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [
      axiosClient,
      setStudents,
      setSelectedStudent,
      handleApiError,
      handleApiSuccess,
      clearErrors,
    ]
  );

  const updateStudent = useCallback(
    async (
      id: string,
      student: StudentFormData,
      onSuccess?: (student: Student) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        // Convert StudentFormData to Student for API call
        const studentForApi = student as unknown as Student;
        const result = await new ApiService<Student>(
          "/students",
          axiosClient
        ).update(id, studentForApi);
        if (result.succeeded) {
          setStudents((prev: Student[]) => {
            return prev.map((s) => {
              if (s.id === id) {
                const updatedStudent = {
                  ...s,
                  ...student,
                  gender: student.gender as number,
                  status: student.status,
                };
                setSelectedStudent(updatedStudent);
                return updatedStudent;
              }
              return s;
            });
          });
          handleApiSuccess("Student updated successfully");
          onSuccess?.(student as any);
          return { result };
        } else {
          handleApiError(result, "Failed to update student");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update student");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update student",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [
      axiosClient,
      setStudents,
      setSelectedStudent,
      handleApiError,
      handleApiSuccess,
      clearErrors,
    ]
  );

  const deleteStudent = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Student>(
          "/students",
          axiosClient
        ).delete(id);
        if (result.succeeded) {
          setStudents((prev: Student[]) => prev.filter((s) => s.id !== id));
          setSelectedStudent(null);
          handleApiSuccess("Student deleted successfully");
          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete student");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to delete student");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to delete student",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [
      axiosClient,
      setStudents,
      setSelectedStudent,
      handleApiError,
      handleApiSuccess,
      clearErrors,
    ]
  );

  const bulkImportStudents = useCallback(
    async (
      file: File,
      onSuccess?: (result: any) => void
    ): Promise<{ result: ApiDataResponse<any> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const formData = new FormData();
        formData.append("file", file);

        const result = await axiosClient.post<ApiDataResponse<any>>(
          "bulkimport/import-students",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (result.data?.succeeded) {
          // Update students state with successfully imported students
          if (
            result.data.data.successfullyImportedStudents &&
            result.data.data.successfullyImportedStudents.length > 0
          ) {
            setStudents((prevStudents) => [
              ...prevStudents,
              ...result.data.data.successfullyImportedStudents,
            ]);

            // Set the last imported student as the selected student
            const lastImportedStudent =
              result.data.data.successfullyImportedStudents[
                result.data.data.successfullyImportedStudents.length - 1
              ];
            setSelectedStudent(lastImportedStudent);
          }
          handleApiSuccess("Students imported successfully");
          onSuccess?.(result.data.data);
          return { result: result.data };
        } else {
          handleApiError(result.data, "Failed to import students");
          return { result: result.data };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to import students");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to import students",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: {},
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [
      axiosClient,
      setStudents,
      setSelectedStudent,
      handleApiError,
      handleApiSuccess,
      clearErrors,
    ]
  );

  const downloadTemplate = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    clearErrors();

    try {
      const response = await axiosClient.get(
        "bulkimport/download-student-template",
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "student-upload-template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      handleApiSuccess("Template downloaded successfully");
    } catch (error: any) {
      handleApiError(error, "Could not download template");
    } finally {
      setIsLoading(false);
    }
  }, [axiosClient, handleApiError, handleApiSuccess, clearErrors]);

  return {
    createStudent,
    updateStudent,
    deleteStudent,
    bulkImportStudents,
    downloadTemplate,
    isLoading,
    errors,
    clearErrors,
  };
};
